import { z } from 'zod';
import { PricingTerm } from '@/constants/pricingTypes';
import { AttributeKinds } from '@/constants/attributeTypes';

// Attribute selection variants
export const declinedAttributeSelection = z.object({
  description: z.string(),
  declined: z.boolean().optional(),
});

export const limitsAttributeSelection = z.object({
  description: z.string(),
  perDay: z.number().nullable().optional(),
  perOccurrence: z.number().nullable().optional(),
  perPerson: z.number().nullable().optional(),
});

export const deductibleAttributeSelection = z.object({
  description: z.string(),
  deductible: z.number().optional(),
});

export const attributeSelection = z.union([
  limitsAttributeSelection,
  deductibleAttributeSelection,
  declinedAttributeSelection,
]);

const declinedAvailableAttribute = z.object({
  kind: z.literal(AttributeKinds.DECLINED),
  name: z.string(),
  selections: z.array(declinedAttributeSelection),
});

const limitsAvailableAttribute = z.object({
  kind: z.literal(AttributeKinds.LIMITS),
  name: z.string(),
  selections: z.array(limitsAttributeSelection),
});

const deductibleAvailableAttribute = z.object({
  kind: z.literal(AttributeKinds.DEDUCTIBLE),
  name: z.string(),
  selections: z.array(deductibleAttributeSelection),
});

export const availableAttribute = z.discriminatedUnion('kind', [
  declinedAvailableAttribute,
  limitsAvailableAttribute,
  deductibleAvailableAttribute,
]);

export const attributeRequirement = z.object({
  fulfilled: z.boolean(),
  kind: z.string(),
  message: z.string().nullable(),
});

function createValidationResponse<T extends z.ZodTypeAny>(valueSchema: T) {
  return z.object({
    value: valueSchema,
    valid: z.boolean(),
    message: z.string().nullable(),
    requirements: z.array(attributeRequirement),
  });
}

const declinedQuoteAttribute = z.object({
  kind: z.literal(AttributeKinds.DECLINED),
  selection: createValidationResponse(declinedAttributeSelection),
  selectionAlternatives: z.unknown().nullable(),
});

const limitsQuoteAttribute = z.object({
  kind: z.literal(AttributeKinds.LIMITS),
  selection: createValidationResponse(limitsAttributeSelection),
  selectionAlternatives: z.unknown().nullable(),
});

const deductibleQuoteAttribute = z.object({
  kind: z.literal(AttributeKinds.DEDUCTIBLE),
  selection: createValidationResponse(deductibleAttributeSelection),
  selectionAlternatives: z.unknown().nullable(),
});

export const quoteAttribute = z.discriminatedUnion('kind', [
  declinedQuoteAttribute,
  limitsQuoteAttribute,
  deductibleQuoteAttribute,
]);

export const coverageDetails = z.object({
  initial_term_selection: z.enum(PricingTerm),
  driver_count: z.number(),
  vehicle_count: z.number(),
  monthly_in_cents: z.number(),
  full_term_in_cents: z.number(),
  full_term_savings_in_cents: z.number(),
  due_today_monthly_in_dollars: z.number(),
  due_today_full_term_in_dollars: z.number(),
  policyholder_first_name: z.string(),
});

export const coverageValidation = z.object({
  valid: z.boolean(),
  message: z.string().nullable(),
  requirements: z.array(attributeRequirement),
});

export const coverage = z.object({
  symbol: z.string(),
  scope: z.string(),
  attributes: z.array(quoteAttribute),
  validation: coverageValidation.optional(),
});

export const quoteState = z.object({
  coverages: z.array(coverage),
  details: coverageDetails,
  coverage_rules: z.object().optional(),
  error: z.string().optional(),
});

export type QuoteAttribute = z.infer<typeof quoteAttribute>;
export type AttributeSelection = z.infer<typeof attributeSelection>;
export type AvailableAttribute = z.infer<typeof availableAttribute>;
export type CoverageDetails = z.infer<typeof coverageDetails>;
export type CoverageValidation = z.infer<typeof coverageValidation>;
export type QuoteState = z.infer<typeof quoteState>;
